import { ApiError } from '@hostesa/api-client';
import { fetchMe, useAuth } from '@hostesa/auth-client';
import { useT } from '@hostesa/i18n';
import { Button, Card, CardContent, CardHeader, CardTitle, Input, Label } from '@hostesa/ui';
import { useForm } from '@tanstack/react-form';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useState } from 'react';
import { z } from 'zod';
import { isOperator } from '../../lib/operator';
import { useRuntime } from '../../lib/runtime-context';

/**
 * Operator login screen — the only unauthenticated data-entry route.
 *
 * On success it loads memberships and redirects to the `next` param (if it is a
 * safe in-app path) or the operator landing (`/tenants`). A user WITHOUT a
 * `hostesa_admin` membership is sent to the no-access state rather than into the
 * console. Errors branch on the typed `ApiError` code/status (never on message
 * text) and are announced with text, never colour alone.
 */
export function LoginPage() {
  const { t } = useT('backoffice');
  const { t: tc } = useT('common');
  const auth = useAuth();
  const runtime = useRuntime();
  const navigate = useNavigate();
  const search = useSearch({ from: '/login' });
  const [formError, setFormError] = useState<string | null>(null);

  const emailSchema = z.string().min(1, t('login.emailRequired')).email(t('login.emailInvalid'));
  const passwordSchema = z.string().min(1, t('login.passwordRequired'));

  const form = useForm({
    defaultValues: { email: '', password: '' },
    onSubmit: async ({ value }) => {
      setFormError(null);
      try {
        await auth.login({ email: value.email, password: value.password });

        // Load memberships to compute the post-login destination.
        const me = await fetchMe(runtime.apiClient);
        const next = typeof search.next === 'string' ? search.next : undefined;
        const safeNext = next?.startsWith('/') && !next.startsWith('//') ? next : undefined;
        // Operators land on /tenants; the OperatorGuard rejects non-operators.
        const target = isOperator(me.memberships) ? (safeNext ?? '/tenants') : '/tenants';

        await navigate({ to: target });
      } catch (err) {
        if (err instanceof ApiError) {
          // Branch on stable code/status, not the (localised) message.
          if (err.status === 401) {
            setFormError(tc('auth.errors.invalidCredentials'));
          } else if (err.code === 'invalid_response') {
            setFormError(tc('auth.errors.unknown'));
          } else {
            setFormError(tc('auth.errors.networkError'));
          }
        } else {
          setFormError(tc('auth.errors.unknown'));
        }
      }
    },
  });

  return (
    <main className="flex min-h-screen items-center justify-center bg-[var(--color-canvas)] px-4">
      <Card className="w-full max-w-sm" data-testid="login-card">
        <CardHeader>
          <CardTitle>{t('login.title')}</CardTitle>
          <p className="text-sm text-[var(--color-stone-mid)]">{t('login.subtitle')}</p>
        </CardHeader>
        <CardContent>
          <form
            data-testid="login-form"
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              void form.handleSubmit();
            }}
            className="flex flex-col gap-4"
          >
            <form.Field
              name="email"
              validators={{ onBlur: emailSchema }}
              // biome-ignore lint/correctness/noChildrenProp: TanStack Form's field render-prop API
              children={(field) => (
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="login-email">{tc('auth.email')}</Label>
                  <Input
                    id="login-email"
                    data-testid="login-email-input"
                    type="email"
                    autoComplete="email"
                    placeholder={tc('auth.emailPlaceholder')}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    aria-invalid={field.state.meta.errors.length > 0}
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p
                      data-testid="login-email-error"
                      role="alert"
                      className="text-xs text-[var(--color-alert)]"
                    >
                      {String(field.state.meta.errors[0]?.message ?? field.state.meta.errors[0])}
                    </p>
                  )}
                </div>
              )}
            />

            <form.Field
              name="password"
              validators={{ onBlur: passwordSchema }}
              // biome-ignore lint/correctness/noChildrenProp: TanStack Form's field render-prop API
              children={(field) => (
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="login-password">{tc('auth.password')}</Label>
                  <Input
                    id="login-password"
                    data-testid="login-password-input"
                    type="password"
                    autoComplete="current-password"
                    placeholder={tc('auth.passwordPlaceholder')}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    aria-invalid={field.state.meta.errors.length > 0}
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p
                      data-testid="login-password-error"
                      role="alert"
                      className="text-xs text-[var(--color-alert)]"
                    >
                      {String(field.state.meta.errors[0]?.message ?? field.state.meta.errors[0])}
                    </p>
                  )}
                </div>
              )}
            />

            {formError && (
              <p
                data-testid="login-form-error"
                role="alert"
                className="rounded-md border border-[var(--color-alert)] bg-[var(--color-canvas)] px-3 py-2 text-sm text-[var(--color-alert)]"
              >
                {formError}
              </p>
            )}

            <form.Subscribe
              selector={(state) => state.isSubmitting}
              // biome-ignore lint/correctness/noChildrenProp: TanStack Form's Subscribe render-prop API
              children={(isSubmitting) => (
                <Button
                  type="submit"
                  data-testid="login-submit-btn"
                  disabled={isSubmitting}
                  className="w-full"
                >
                  {isSubmitting ? t('login.submitting') : tc('auth.signIn')}
                </Button>
              )}
            />
          </form>
        </CardContent>
      </Card>
    </main>
  );
}
